import { DisplayNameSchema, type UserPrivate } from '@runway/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../../net/api';
import { useSession } from '../../state/session';
import { Button } from '../../ui/Button';
import { FullScreenSpinner } from '../../ui/Spinner';
import { cx } from '../../ui/cx';
import './login.css';

const ERRORS: Record<string, string> = {
  state: 'That sign-in link expired. Please try again.',
  denied: 'Google sign-in was cancelled.',
  not_allowed: 'This Google account is not on the guest list. Ask the host to add you.',
  google: 'Google sign-in failed. Please try again.',
};

/** Only same-origin relative paths as post-login destinations. */
function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/login')) return '/';
  return raw;
}

const SPARKLES = [
  { top: '12%', left: '8%', size: 22, delay: '0s' },
  { top: '22%', right: '12%', size: 16, delay: '-1.2s' },
  { bottom: '18%', left: '14%', size: 14, delay: '-2.1s' },
  { bottom: '26%', right: '8%', size: 26, delay: '-0.6s' },
  { top: '55%', left: '4%', size: 12, delay: '-3s' },
];

export function LoginPage() {
  const status = useSession((s) => s.status);
  const devLoginEnabled = useSession((s) => s.devLoginEnabled);
  const googleEnabled = useSession((s) => s.googleEnabled);
  const sessionError = useSession((s) => s.error);
  const refresh = useSession((s) => s.refresh);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = safeNext(params.get('next'));
  const urlError = params.get('error');

  const [name, setName] = useState(() => localStorage.getItem('runway:lastName') ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(urlError ? (ERRORS[urlError] ?? 'Sign-in failed.') : null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (status === 'authed') navigate(next, { replace: true });
  }, [status, next, navigate]);

  if (status === 'loading') return <FullScreenSpinner label="Checking your ticket…" />;

  const submitDev = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = DisplayNameSchema.safeParse(name);
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Pick a longer name');
      return;
    }
    setError(null);
    setFieldError(null);
    setBusy(true);
    try {
      await api.post<{ user: UserPrivate }>('/auth/dev', { displayName: parsed.data });
      localStorage.setItem('runway:lastName', parsed.data);
      await refresh(); // flips status → 'authed' → effect above navigates
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server. Is it running?');
    } finally {
      setBusy(false);
    }
  };

  const nothingConfigured = !googleEnabled && !devLoginEnabled;

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden="true" />
      <div className="login-grain" aria-hidden="true" />
      {SPARKLES.map((s, i) => (
        <span key={i} className="login-sparkle" aria-hidden="true" style={{ top: s.top, left: s.left, right: s.right, bottom: s.bottom, fontSize: s.size, animationDelay: `${s.delay}, ${s.delay}` }}>
          ✦
        </span>
      ))}

      <main className="login-card stack" style={{ gap: 'var(--sp-6)' }}>
        <header style={{ textAlign: 'center' }}>
          <div className="login-mannequins" aria-hidden="true">
            <span className="mannequin" style={{ '--m1': '#ff3e9d', '--m2': '#e0207f', '--skin': '#D19E73' } as React.CSSProperties} />
            <span className="mannequin" style={{ '--m1': '#ffcc4d', '--m2': '#e0ad2a', '--skin': '#7F4A2E' } as React.CSSProperties} />
            <span className="mannequin" style={{ '--m1': '#cdb4ff', '--m2': '#7c4dff', '--skin': '#F3D9C4' } as React.CSSProperties} />
          </div>
          <h1 className="brand login-wordmark" style={{ margin: 0 }}>
            <span className="brand-spark" aria-hidden="true">
              ✦
            </span>
            RUNWAY
          </h1>
          <p className="login-tagline">
            Dress up. Walk the runway. Get judged <em>(nicely)</em>.
          </p>
        </header>

        <section className="login-panel stack" aria-label="Sign in">
          {error || (sessionError && nothingConfigured) ? (
            <div className="help help-error" role="alert" style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--danger-soft)' }}>
              {error ?? sessionError}
            </div>
          ) : null}

          {googleEnabled ? (
            <a className="btn btn-primary btn-lg btn-block" href={`/auth/google?next=${encodeURIComponent(next)}`}>
              <GoogleMark /> Sign in with Google
            </a>
          ) : null}

          {googleEnabled && devLoginEnabled ? <div className="divider">or play on this network</div> : null}

          {devLoginEnabled ? (
            <form className="stack" onSubmit={submitDev} noValidate>
              {!googleEnabled ? (
                <div>
                  <h3 style={{ marginBottom: 4 }}>What should we call you?</h3>
                  <p className="help">No password — this is a private game for friends on this network.</p>
                </div>
              ) : null}
              <label className="field">
                <span className="label">Display name</span>
                <input
                  className={cx('input', fieldError && 'input-error')}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setFieldError(null);
                  }}
                  placeholder="e.g. Zoe"
                  maxLength={24}
                  autoComplete="nickname"
                  autoFocus
                  aria-invalid={fieldError ? true : undefined}
                  aria-describedby="name-help"
                />
                <span id="name-help" className={cx('help', fieldError && 'help-error')}>
                  {fieldError ?? '2–24 characters. Use the same name each time to get your looks back — anyone on this network who types it gets that account.'}
                </span>
              </label>
              <Button type="submit" variant="gold" size="lg" block loading={busy} disabled={name.trim().length < 2}>
                Let&apos;s go ✨
              </Button>
            </form>
          ) : null}

          {nothingConfigured ? (
            <div className="empty">
              <div className="empty-emoji" aria-hidden="true">
                🔐
              </div>
              <h4>Sign-in is not set up</h4>
              <p>
                {sessionError ? 'The server could not be reached.' : 'Set '}
                {!sessionError ? (
                  <>
                    <code>AUTH_DEV_LOGIN=true</code> or Google credentials in <code>.env</code>.
                  </>
                ) : null}
              </p>
              <Button size="sm" variant="ghost" onClick={() => void refresh()}>
                Try again
              </Button>
            </div>
          ) : null}
        </section>

        <p className="login-foot">A tiny fashion-show game for a few friends. No ads, no strangers.</p>
      </main>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.4 35.4 44 30 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}
